import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  X,
  Plus,
  Save,
  ChevronLeft,
  Star,
  Users,
  Edit3,
} from "lucide-react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import ilceKoyler from "../json/ilce-koyler.json";
import { UNLU_KATEGORI_DATA, ANA_KATEGORILER } from "../hooks/unluKategoriData";

const cx = (...list) => list.filter(Boolean).join(" ");

function AInput({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition"
    />
  );
}

function ASelect({ value, onChange, children, disabled }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition
        appearance-none bg-white disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
    >
      {children}
    </select>
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        placeholder:text-slate-400 transition resize-none"
    />
  );
}

function Field({ label, children, span2 }) {
  return (
    <div className={cx(span2 && "md:col-span-2")}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
        {children}
      </span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

/* ── Ünlü form mapper ── */
function celebToForm(id, d) {
  return {
    _fbId: id,
    name: d.name || "",
    kategori: d.kategori || "",
    profession: d.profession || "",
    birthYear: d.birthYear || "",
    sivasIlce: d.sivasIlce || "",
    sivasKoy: d.sivasKoy || "",
    gender: d.gender || "",
    bio: d.bio || "",
    imageUrl: d.imageUrl || "",
    isActive: d.isActive || false,
    isPassive: d.isPassive || false,
    isDeceased: d.isDeceased || false,
    tags: d.tags || [],
    tagInput: "",
  };
}

/* ── Siyasetçi form mapper ── */
function politicianToForm(id, d) {
  return {
    _fbId: id,
    name: d.name || "",
    party: d.party || "",
    position: d.position || "",
    birthYear: d.birthYear || "",
    sivasIlce: d.sivasIlce || "",
    sivasKoy: d.sivasKoy || "",
    gender: d.gender || "",
    bio: d.bio || "",
    imageUrl: d.imageUrl || "",
    isActive: d.isActive || false,
    isPassive: d.isPassive || false,
    isDeceased: d.isDeceased || false,
    tags: d.tags || [],
    tagInput: "",
  };
}

export default function CelebPoliticianEditTab({
  loading,
  setLoading,
  showStatus,
}) {
  /* ── Tür seçimi ── */
  const [mode, setMode] = useState("celeb"); // celeb | politician

  /* ── Veriler ── */
  const [celebs, setCelebs] = useState([]);
  const [politicians, setPoliticians] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const toSentenceCase = (s = "") =>
    s.charAt(0).toLocaleUpperCase("tr") + s.slice(1).toLocaleLowerCase("tr");

  useEffect(() => {
    const q = query(collection(db, "unluler"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setCelebs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDataLoading(false);
      },
      () => setDataLoading(false)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "siyasiler"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setPoliticians(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  /* ── Arama & seçim ── */
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // form objesi

  const list = mode === "celeb" ? celebs : politicians;

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return list;
    return list.filter((item) =>
      `${item.name} ${item.profession || item.position || ""} ${
        item.party || ""
      }`
        .toLocaleLowerCase("tr")
        .includes(q)
    );
  }, [list, search]);

  /* ── İlçe/Köy (siyasetçi için) ── */
  const ilceler = useMemo(
    () =>
      [...new Set(ilceKoyler.map((x) => x.ilce))].sort((a, b) =>
        a.localeCompare(b, "tr")
      ),
    []
  );
  const koyler = useMemo(() => {
    if (!selected?.sivasIlce) return [];
    const list = ilceKoyler
      .filter((x) => x.ilce === selected.sivasIlce)
      .map((x) => x.koy);
    const merkez = list.filter((k) =>
      k.toLocaleLowerCase("tr").includes("merkez")
    );
    const diger = list
      .filter((k) => !k.toLocaleLowerCase("tr").includes("merkez"))
      .sort((a, b) => a.localeCompare(b, "tr"));
    return [...merkez, ...diger];
  }, [selected?.sivasIlce]);

  const set = (patch) => setSelected((p) => ({ ...p, ...patch }));

  const handleSelect = (item) => {
    if (mode === "celeb") setSelected(celebToForm(item.id, item));
    else setSelected(politicianToForm(item.id, item));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`"${item.name}" silinsin mi?`)) return;
    try {
      const col = mode === "celeb" ? "unluler" : "siyasiler";
      await deleteDoc(doc(db, col, item.id));
      showStatus("success", "Kayıt silindi.");
    } catch (err) {
      showStatus("error", "Silinemedi: " + err.message);
    }
  };

  /* ── Kaydet ── */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      const col = mode === "celeb" ? "unluler" : "siyasiler";
      const { _fbId, tagInput, ...rest } = selected;
      await updateDoc(doc(db, col, _fbId), {
        ...rest,
        updatedAt: serverTimestamp(),
      });
      showStatus("success", "Güncellendi.");
      setSelected(null);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ════ RENDER ════ */

  /* ── Düzenleme formu ── */
  if (selected)
    return (
      <div>
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition"
        >
          <ChevronLeft className="w-4 h-4" /> Listeye Dön
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Edit3 className="w-4 h-4 text-amber-500" />
          <h3 className="font-semibold text-slate-900 text-sm">
            {mode === "celeb" ? "Ünlü Düzenle" : "Siyasetçi Düzenle"}
          </h3>
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          {mode === "celeb" ? (
            /* ── ÜNLÜ FORMU ── */
            <>
              <SectionHeading>Temel Bilgiler</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="İsim Soyisim" span2>
                  <AInput
                    value={selected.name}
                    onChange={(e) => set({ name: e.target.value })}
                    placeholder="Ad Soyad"
                  />
                </Field>
                <Field label="Kategori">
                  <ASelect
                    value={selected.kategori}
                    onChange={(e) =>
                      set({ kategori: e.target.value, profession: "" })
                    }
                  >
                    <option value="">Kategori seçin…</option>
                    {ANA_KATEGORILER.filter((k) => k !== "siyasiler").map(
                      (k) => (
                        <option key={k} value={k}>
                          {toSentenceCase(k)}
                        </option>
                      )
                    )}
                  </ASelect>
                </Field>

                <Field label="Meslek / Ünvan">
                  {(UNLU_KATEGORI_DATA[selected.kategori] || []).length > 0 && (
                    <ASelect
                      value=""
                      onChange={(e) => {
                        if (e.target.value) set({ profession: e.target.value });
                      }}
                    >
                      <option value="">Alt kategori seçin…</option>
                      {(UNLU_KATEGORI_DATA[selected.kategori] || []).map(
                        (a) => (
                          <option key={a} value={a}>
                            {toSentenceCase(a)}
                          </option>
                        )
                      )}
                    </ASelect>
                  )}
                  <div
                    className={
                      (UNLU_KATEGORI_DATA[selected.kategori] || []).length > 0
                        ? "mt-2"
                        : ""
                    }
                  >
                    <AInput
                      value={selected.profession}
                      onChange={(e) => set({ profession: e.target.value })}
                      placeholder={
                        (UNLU_KATEGORI_DATA[selected.kategori] || []).length > 0
                          ? "Veya serbest yazın…"
                          : "Kategori seçin veya direkt yazın…"
                      }
                    />
                  </div>
                  {selected.profession && (
                    <p className="text-xs text-slate-400 mt-1">
                      Seçilen:{" "}
                      <span className="font-medium text-slate-600">
                        {selected.profession}
                      </span>
                    </p>
                  )}
                </Field>
                <Field label="Doğum Yılı">
                  <AInput
                    value={selected.birthYear}
                    onChange={(e) => set({ birthYear: e.target.value })}
                    placeholder="1970"
                  />
                </Field>
                <Field label="Doğum Yeri" span2>
                  <SectionHeading>Köken & Cinsiyet</SectionHeading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="İlçe (Sivas)">
                      <ASelect
                        value={selected.sivasIlce}
                        onChange={(e) =>
                          set({ sivasIlce: e.target.value, sivasKoy: "" })
                        }
                      >
                        <option value="">İlçe seçiniz…</option>
                        {ilceler.map((il) => (
                          <option key={il} value={il}>
                            {il}
                          </option>
                        ))}
                      </ASelect>
                    </Field>
                    <Field label="Köy / Mahalle">
                      <ASelect
                        value={selected.sivasKoy}
                        onChange={(e) => set({ sivasKoy: e.target.value })}
                        disabled={!selected.sivasIlce}
                      >
                        <option value="">
                          {selected.sivasIlce
                            ? "Köy seçiniz…"
                            : "Önce ilçe seçiniz"}
                        </option>
                        {koyler.map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                      </ASelect>
                    </Field>
                  </div>
                  <Field label="Cinsiyet">
                    <div className="flex gap-3">
                      {[
                        { value: "Erkek", label: "Erkek" },
                        { value: "Kadın", label: "Kadın" },
                      ].map(({ value, label }) => (
                        <label
                          key={value}
                          className={cx(
                            "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border cursor-pointer text-sm font-medium transition",
                            selected.gender === value
                              ? "bg-slate-900 text-white border-slate-900"
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"
                          )}
                        >
                          <input
                            type="radio"
                            className="hidden"
                            name="celeb-gender"
                            value={value}
                            checked={selected.gender === value}
                            onChange={() => set({ gender: value })}
                          />
                          {label}
                        </label>
                      ))}
                      {selected.gender && (
                        <button
                          type="button"
                          onClick={() => set({ gender: "" })}
                          className="px-3 py-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 text-xs transition"
                        >
                          Temizle
                        </button>
                      )}
                    </div>
                  </Field>
                </Field>
              </div>

              <SectionHeading>Biyografi</SectionHeading>
              <ATextarea
                value={selected.bio}
                onChange={(e) => set({ bio: e.target.value })}
                rows={4}
                placeholder="Kısa biyografi…"
              />

              <SectionHeading>Fotoğraf</SectionHeading>
              <AInput
                value={selected.imageUrl}
                onChange={(e) => set({ imageUrl: e.target.value })}
                placeholder="https://…"
              />

              <SectionHeading>Durum</SectionHeading>
              <div className="flex flex-wrap gap-3">
                {[
                  {
                    key: "isActive",
                    label: "Görevde",
                    color: "bg-emerald-500",
                  },
                  {
                    key: "isPassive",
                    label: "Görevde Değil",
                    color: "bg-slate-400",
                  },
                  { key: "isDeceased", label: "Vefat", color: "bg-red-400" },
                ].map(({ key, label, color }) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 cursor-pointer w-fit"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selected[key]}
                        onChange={(e) => set({ [key]: e.target.checked })}
                      />
                      <div
                        className={cx(
                          "w-11 h-6 rounded-full transition-colors duration-200",
                          selected[key] ? color : "bg-slate-200"
                        )}
                      >
                        <div
                          className={cx(
                            "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                            selected[key] ? "translate-x-5" : "translate-x-0"
                          )}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      {label}
                    </span>
                  </label>
                ))}
              </div>

              <SectionHeading>Etiketler</SectionHeading>
              <div className="flex gap-2">
                <AInput
                  value={selected.tagInput}
                  onChange={(e) => set({ tagInput: e.target.value })}
                  placeholder="Etiket ekle…"
                />
                <button
                  type="button"
                  onClick={() => {
                    const t = selected.tagInput.trim();
                    if (!t || selected.tags.includes(t))
                      return set({ tagInput: "" });
                    set({ tags: [...selected.tags, t], tagInput: "" });
                  }}
                  className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Ekle
                </button>
              </div>
              {selected.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selected.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() =>
                          set({ tags: selected.tags.filter((x) => x !== t) })
                        }
                        className="text-amber-600 hover:text-red-600 transition"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* ── SİYASETÇİ FORMU ── */
            <>
              <SectionHeading>Temel Bilgiler</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="İsim Soyisim" span2>
                  <AInput
                    value={selected.name}
                    onChange={(e) => set({ name: e.target.value })}
                    placeholder="Ad Soyad"
                  />
                </Field>
                <Field label="Parti">
                  <AInput
                    value={selected.party}
                    onChange={(e) => set({ party: e.target.value })}
                    placeholder="CHP, AKP, MHP…"
                  />
                </Field>
                <Field label="Görev / Pozisyon">
                  <ASelect
                    value=""
                    onChange={(e) => {
                      if (e.target.value) set({ position: e.target.value });
                    }}
                  >
                    <option value="">Görev seçin…</option>
                    {(UNLU_KATEGORI_DATA["siyasiler"] || []).map((p) => (
                      <option key={p} value={p}>
                        {toSentenceCase(p)}
                      </option>
                    ))}
                  </ASelect>
                  <div className="mt-2">
                    <AInput
                      value={selected.position}
                      onChange={(e) => set({ position: e.target.value })}
                      placeholder="Veya serbest yazın…"
                    />
                  </div>
                  {selected.position && (
                    <p className="text-xs text-slate-400 mt-1">
                      Seçilen:{" "}
                      <span className="font-medium text-slate-600">
                        {selected.position}
                      </span>
                    </p>
                  )}
                </Field>
                <Field label="Doğum Yılı">
                  <AInput
                    value={selected.birthYear}
                    onChange={(e) => set({ birthYear: e.target.value })}
                    placeholder="1965"
                  />
                </Field>
              </div>

              <SectionHeading>Köken</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="İlçe (Sivas)">
                  <ASelect
                    value={selected.sivasIlce}
                    onChange={(e) =>
                      set({ sivasIlce: e.target.value, sivasKoy: "" })
                    }
                  >
                    <option value="">İlçe seçiniz…</option>
                    {ilceler.map((il) => (
                      <option key={il} value={il}>
                        {il}
                      </option>
                    ))}
                  </ASelect>
                </Field>
                <Field label="Köy / Mahalle">
                  <ASelect
                    value={selected.sivasKoy}
                    onChange={(e) => set({ sivasKoy: e.target.value })}
                    disabled={!selected.sivasIlce}
                  >
                    <option value="">
                      {selected.sivasIlce
                        ? "Köy seçiniz…"
                        : "Önce ilçe seçiniz"}
                    </option>
                    {koyler.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </ASelect>
                </Field>
              </div>

              <SectionHeading>Cinsiyet</SectionHeading>
              <div className="flex gap-3">
                {[
                  { value: "Erkek", label: "Erkek" },
                  { value: "Kadın", label: "Kadın" },
                ].map(({ value, label }) => (
                  <label
                    key={value}
                    className={cx(
                      "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border cursor-pointer text-sm font-medium transition",
                      selected.gender === value
                        ? "bg-slate-900 text-white border-slate-900"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    <input
                      type="radio"
                      className="hidden"
                      name="edit-gender"
                      value={value}
                      checked={selected.gender === value}
                      onChange={() => set({ gender: value })}
                    />
                    {label}
                  </label>
                ))}
                {selected.gender && (
                  <button
                    type="button"
                    onClick={() => set({ gender: "" })}
                    className="px-3 py-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 text-xs transition"
                  >
                    Temizle
                  </button>
                )}
              </div>

              <SectionHeading>Biyografi</SectionHeading>
              <ATextarea
                value={selected.bio}
                onChange={(e) => set({ bio: e.target.value })}
                rows={4}
                placeholder="Kısa biyografi…"
              />

              <SectionHeading>Fotoğraf</SectionHeading>
              <AInput
                value={selected.imageUrl}
                onChange={(e) => set({ imageUrl: e.target.value })}
                placeholder="https://…"
              />

              <SectionHeading>Durum</SectionHeading>
              <div className="flex flex-wrap gap-3">
                {[
                  {
                    key: "isActive",
                    label: "Görevde",
                    color: "bg-emerald-500",
                  },
                  {
                    key: "isPassive",
                    label: "Görevde Değil",
                    color: "bg-slate-400",
                  },
                  { key: "isDeceased", label: "Vefat", color: "bg-red-400" },
                ].map(({ key, label, color }) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 cursor-pointer w-fit"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selected[key]}
                        onChange={(e) => set({ [key]: e.target.checked })}
                      />
                      <div
                        className={cx(
                          "w-11 h-6 rounded-full transition-colors duration-200",
                          selected[key] ? color : "bg-slate-200"
                        )}
                      >
                        <div
                          className={cx(
                            "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                            selected[key] ? "translate-x-5" : "translate-x-0"
                          )}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      {label}
                    </span>
                  </label>
                ))}
              </div>

              <SectionHeading>Etiketler</SectionHeading>
              <div className="flex gap-2">
                <AInput
                  value={selected.tagInput}
                  onChange={(e) => set({ tagInput: e.target.value })}
                  placeholder="Etiket ekle…"
                />
                <button
                  type="button"
                  onClick={() => {
                    const t = selected.tagInput.trim();
                    if (!t || selected.tags.includes(t))
                      return set({ tagInput: "" });
                    set({ tags: [...selected.tags, t], tagInput: "" });
                  }}
                  className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Ekle
                </button>
              </div>
              {selected.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selected.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-xs"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() =>
                          set({ tags: selected.tags.filter((x) => x !== t) })
                        }
                        className="text-slate-400 hover:text-red-500 transition"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="pt-2 flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {loading ? "Kaydediliyor…" : "Güncelle"}
            </button>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    );

  /* ── Liste görünümü ── */
  return (
    <div>
      {/* Tür toggle */}
      <div className="flex gap-2 mb-4">
        {[
          { v: "celeb", label: "Ünlüler", icon: Star },
          { v: "politician", label: "Siyasiler", icon: Users },
        ].map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            type="button"
            onClick={() => {
              setMode(v);
              setSearch("");
              setSelected(null);
            }}
            className={cx(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition",
              mode === v
                ? "bg-slate-900 text-white border-slate-900"
                : "border-slate-200 text-slate-600 hover:bg-slate-50"
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            <span
              className={cx(
                "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                mode === v
                  ? "bg-white text-slate-900"
                  : "bg-slate-100 text-slate-500"
              )}
            >
              {v === "celeb" ? celebs.length : politicians.length}
            </span>
          </button>
        ))}
      </div>

      {/* Arama */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            mode === "celeb"
              ? "İsim veya meslek ara…"
              : "İsim, parti veya görev ara…"
          }
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 mb-3">
        <span className="font-medium text-slate-600">{filtered.length}</span>{" "}
        kayıt listeleniyor
      </p>

      {dataLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-14 bg-slate-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      )}

      {!dataLoading && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200
                hover:border-slate-300 hover:shadow-sm transition text-left group"
            >
              {/* Avatar */}
              <div className="w-10 h-10 rounded-lg border border-slate-100 bg-gradient-to-br from-amber-100 to-amber-200 overflow-hidden shrink-0 flex items-center justify-center text-xs font-bold text-amber-800">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  (item.name || "?")
                    .split(" ")
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase()
                )}
              </div>

              {/* Bilgi */}
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-slate-900 truncate block">
                  {item.name || "—"}
                </span>
                <span className="text-xs text-slate-400 truncate block">
                  {mode === "celeb"
                    ? item.profession || "—"
                    : [item.position, item.party].filter(Boolean).join(" · ") ||
                      "—"}
                </span>
              </div>

              {/* Durum badge (sadece siyasetçi) */}
              {mode === "politician" && (
                <span
                  className={cx(
                    "text-[10px] px-1.5 py-0.5 rounded-full border font-medium shrink-0",
                    item.isActive
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : item.isDeceased
                      ? "bg-slate-100 text-slate-500 border-slate-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                  )}
                >
                  {item.isActive
                    ? "Görevde"
                    : item.isDeceased
                    ? "Vefat"
                    : "Görevde Değil"}
                </span>
              )}

              {/* Sil butonu */}
              <button
                type="button"
                onClick={(e) => handleDelete(e, item)}
                className="shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                title="Sil"
              >
                <X className="w-4 h-4" />
              </button>

              <Edit3 className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 transition" />
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <p className="text-sm">Kayıt bulunamadı.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
